import { getCookie, removeCookie, setCookie } from "@/services/CookiesService";

import { PROVIDER_COOKIE_NAME } from "./core";
import { createProviderCookieStore } from "./store";

import type { ProviderCookieStore } from "./types";

/**
 * Client runtime adapter: the provider cookie over `document.cookie`, via
 * `CookiesService` (which JSON-encodes objects and applies the client cookie
 * flags).
 *
 * Atomicity limit: `update` re-reads the cookie immediately before writing, so a
 * caller's partial cannot be built on a stale snapshot. It cannot be made fully
 * atomic — `document.cookie` offers no compare-and-swap, and the verify-payment
 * iframe and other tabs write the same cookie from separate JS contexts. The
 * mitigation is the interface: pass the smallest possible partial to `update`,
 * and never `replace` with a value read earlier.
 * See docs/adr/0001-provider-cookie-ownership.md.
 */
export const createClientProviderCookieStore = (): ProviderCookieStore =>
  createProviderCookieStore({
    readRaw: () => getCookie(PROVIDER_COOKIE_NAME),
    writeValue: (value) => setCookie(PROVIDER_COOKIE_NAME, value),
    clear: () => removeCookie(PROVIDER_COOKIE_NAME),
  });

/**
 * Shared client store. Stateless (the cookie is the state), so a module-level
 * instance is safe and keeps call sites free of wiring. Hooks that want to be
 * driven by the in-memory store in tests take a `ProviderCookieStore` parameter
 * defaulting to this one.
 */
export const providerCookieStore: ProviderCookieStore = createClientProviderCookieStore();
